import { Link } from 'react-router-dom';
import {
  PencilSquareIcon,
  TrashIcon,
  ClipboardDocumentListIcon,
  UserGroupIcon,
  ShareIcon,
} from '@heroicons/react/24/outline';

/**
 * Project card for the projects dashboard.
 * @param {Object} project
 * @param {string} project.id
 * @param {string} project.name
 * @param {string} project.key
 * @param {number} project.ticketCount
 * @param {boolean} project.is_owner
 * @param {boolean} project.is_shared
 * @param {Function} onEdit
 * @param {Function} onDelete
 * @param {Function} onShare
 */
export default function ProjectCard({ project, onEdit, onDelete, onShare }) {
  const { id, name, key: projectKey, ticketCount = 0, is_owner: isOwner, is_shared: isShared } = project;

  return (
    <div className="relative rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
      {/* Top-right action group */}
      <div className="absolute top-3 right-3 flex items-center gap-1">
        <button
          type="button"
          onClick={() => onShare?.(project)}
          className="p-1.5 rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
          title="Share project"
          aria-label="Share project"
        >
          <ShareIcon className="size-5" />
        </button>
        <button
          type="button"
          onClick={() => onEdit?.(project)}
          className="p-1.5 rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
          title="Edit project"
          aria-label="Edit project"
        >
          <PencilSquareIcon className="size-5" />
        </button>
      </div>

      {/* Card content */}
      <div className="p-5 pt-4">
        <div className="flex items-center gap-2 pr-20">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white truncate" title={name}>
            {name}
          </h3>
          {!isOwner && (
            <span className="shrink-0 text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
              Shared with you
            </span>
          )}
          {isOwner && isShared && (
            <span className="shrink-0 text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">
              Shared
            </span>
          )}
        </div>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Key: <span className="font-medium text-slate-700 dark:text-slate-300">{projectKey}</span>
        </p>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          <span className="font-medium text-slate-700 dark:text-slate-300">{ticketCount}</span> ticket{ticketCount !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Actions */}
      <div className="px-5 pb-5 flex flex-wrap gap-2">
        <Link
          to={`/projects/${id}/plan`}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors"
        >
          <ClipboardDocumentListIcon className="size-4" />
          Plan View
        </Link>
        <Link
          to={`/projects/${id}/capacity`}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 transition-colors"
        >
          <UserGroupIcon className="size-4" />
          Capacity View
        </Link>
        {isOwner && (
          <button
            type="button"
            onClick={() => onDelete?.(project)}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/30 transition-colors"
          >
            <TrashIcon className="size-4" />
            Delete
          </button>
        )}
      </div>
    </div>
  );
}